import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge } from 'react-bootstrap';
import {
  FiUsers,
  FiBriefcase,
  FiCheck,
  FiActivity,
  FiLogIn,
  FiLogOut,
  FiPlusCircle,
  FiEdit3,
  FiTrash2,
  FiClock
} from 'react-icons/fi';
import { userAPI, projectAPI, departmentAPI, roleAPI, auditLogAPI } from '../../utils/api';
import '../styles/AdminDashboard.css';

const getActivityVisual = (action) => {
  const normalizedAction = String(action || '').toLowerCase();
  if (normalizedAction === 'login') {
    return { Icon: FiLogIn, label: 'Login', tone: 'activity-tone-login' };
  }
  if (normalizedAction === 'logout') {
    return { Icon: FiLogOut, label: 'Logout', tone: 'activity-tone-logout' };
  }
  if (normalizedAction === 'create') {
    return { Icon: FiPlusCircle, label: 'Create', tone: 'activity-tone-create' };
  }
  if (normalizedAction === 'update') {
    return { Icon: FiEdit3, label: 'Update', tone: 'activity-tone-update' };
  }
  if (normalizedAction === 'delete') {
    return { Icon: FiTrash2, label: 'Delete', tone: 'activity-tone-delete' };
  }
  return { Icon: FiActivity, label: normalizedAction || 'Activity', tone: 'activity-tone-default' };
};

const formatActivityTime = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const buildAdminDashboardState = ({ usersRes, projectsRes, deptsRes, rolesRes, auditRes }) => {
  const nextStats = {
    totalUsers: 0,
    activeUsers: 0,
    totalProjects: 0,
    activeProjects: 0,
    totalDepartments: 0,
    totalRoles: 0
  };

  const next = {
    stats: nextStats,
    recentActivity: [],
  };

  if (usersRes?.success && usersRes?.data) {
    next.stats.totalUsers = usersRes.data.length;
    next.stats.activeUsers = usersRes.data.filter((u) => u.isActive).length;
  }

  if (projectsRes?.success && projectsRes?.data) {
    next.stats.totalProjects = projectsRes.data.length;
    next.stats.activeProjects = projectsRes.data.filter((p) => p.status === 'active').length;
  }

  if (deptsRes?.success && deptsRes?.data) {
    next.stats.totalDepartments = deptsRes.data.length;
  }

  if (rolesRes?.success && rolesRes?.data) {
    next.stats.totalRoles = rolesRes.data.length;
  }

  if (auditRes?.success && auditRes?.data) {
    next.recentActivity = auditRes.data.slice(0, 5);
  }

  return next;
};

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalProjects: 0,
    activeProjects: 0,
    totalDepartments: 0,
    totalRoles: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      setError('');

      const results = await Promise.allSettled([
        userAPI.getAllUsers(),
        projectAPI.getAllProjects(),
        departmentAPI.getAllDepartments(),
        roleAPI.getAllRoles(),
        auditLogAPI.getAllAuditLogs(1, 10)
      ]);

      const [usersRes, projectsRes, deptsRes, rolesRes, auditRes] = results.map((r) =>
        r.status === 'fulfilled' ? r.value : null
      );
      const failures = results
        .map((r) => (r.status === 'rejected' ? r.reason : null))
        .filter(Boolean);

      const next = buildAdminDashboardState({ usersRes, projectsRes, deptsRes, rolesRes, auditRes });
      setStats((prev) => ({ ...prev, ...next.stats }));
      setRecentActivity(next.recentActivity);

      if (failures.length) {
        // If user doesn't have permission for some admin widgets, keep the page usable.
        const status = failures.find((f) => f?.statusCode)?.statusCode;
        if (status === 403) setError('Some admin widgets are not accessible with your permissions.');
        else if (status === 429) setError('Too many requests. Please refresh after a moment.');
        else setError('Some admin widgets failed to load.');
      }
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <Container fluid className="admin-dashboard">
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="mb-3 g-2">
        <Col md={2} className="mb-2">
          <StatCard title="Total Users" value={stats.totalUsers} icon={<FiUsers size={24} />} color="primary" />
        </Col>
        <Col md={2} className="mb-2">
          <StatCard title="Active Users" value={stats.activeUsers} icon={<FiActivity size={24} />} color="success" />
        </Col>
        <Col md={2} className="mb-2">
          <StatCard title="Total Projects" value={stats.totalProjects} icon={<FiBriefcase size={24} />} color="info" />
        </Col>
        <Col md={2} className="mb-2">
          <StatCard title="Active Projects" value={stats.activeProjects} icon={<FiCheck size={24} />} color="warning" />
        </Col>
        <Col md={2} className="mb-2">
          <StatCard title="Departments" value={stats.totalDepartments} icon={<FiBriefcase size={24} />} color="secondary" />
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card className="shadow-sm activity-card">
            <Card.Header className="activity-card-header">
              <div className="activity-card-title-wrap">
                <h5 className="mb-0">Recent Activity</h5>
              </div>
            </Card.Header>
            <Card.Body className="activity-card-body">
              {recentActivity.length === 0 ? (
                <div className="activity-empty-state">No recent activity</div>
              ) : (
                <div className="activity-timeline">
                  {recentActivity.map((activity, index) => {
                    const visual = getActivityVisual(activity.action);
                    return (
                      <div key={index} className="activity-item">
                        <div className="activity-rail" />
                        <div className={`activity-icon ${visual.tone}`}>
                          <visual.Icon size={14} />
                        </div>
                        <div className="activity-content">
                          <div className="activity-main-line">
                            <p className="activity-description mb-0">{activity.description || 'No description provided'}</p>
                            <span className="activity-time">
                              <FiClock size={12} />
                              {formatActivityTime(activity.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div className="activity-status-wrap">
                          <Badge
                            className={activity.status === 'success' ? 'activity-status success' : 'activity-status failed'}
                            bg="light"
                            text={activity.status === 'success' ? 'success' : 'danger'}
                          >
                            {activity.status || 'unknown'}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    primary: 'stat-primary',
    success: 'stat-success',
    info: 'stat-info',
    warning: 'stat-warning',
    danger: 'stat-danger',
    secondary: 'stat-secondary'
  };

  return (
    <Card className={`stat-card ${colorClasses[color]} shadow-sm`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <p className="stat-title">{title}</p>
            <h2 className="stat-value">{value}</h2>
          </div>
          <div className="stat-icon">{icon}</div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AdminDashboard;

